/**
 * PickUpJobsTab.js - Owner Job Pickup Component
 * Allows owners to quickly pick up/claim jobs directly from Owner Dashboard
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, MapPin, Clock, DollarSign, ChevronRight,
  CheckCircle, RefreshCw, Briefcase, Car, Sparkles, Truck, ShoppingCart,
  Home, Zap, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { jobsAPI } from '../../services/api';

const SERVICE_ICONS = {
  rides: Car,
  laundry: Sparkles,
  errands: ShoppingCart,
  courier: Truck,
  rides: Car
};

const SERVICE_COLORS = {
  rides: 'from-purple-500 to-purple-600',
  laundry: 'from-purple-500 to-pink-500',
  errands: 'from-orange-500 to-amber-500',
  courier: 'from-green-500 to-emerald-500',
  rides: 'from-indigo-500 to-violet-500'
};

const PickUpJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [stats, setStats] = useState({ total: 0, byType: {} });

  const fetchAvailableJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getAvailable({});
      const jobsList = response.data?.jobs || [];
      setJobs(jobsList);
      
      // Calculate stats
      const byType = {};
      jobsList.forEach(job => {
        const type = job.service_type || 'other';
        byType[type] = (byType[type] || 0) + 1;
      });
      setStats({ total: jobsList.length, byType });
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load available jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailableJobs();
  }, [fetchAvailableJobs]);

  const handleQuickPickUp = async (jobId, jobName) => {
    try {
      setClaiming(jobId);
      await jobsAPI.claim(jobId, { notes: 'Owner quick pickup' });
      toast.success(`You picked up: ${jobName}`);
      
      // Remove from list
      setJobs(prev => prev.filter(j => j.id !== jobId));
      setStats(prev => ({
        ...prev,
        total: prev.total - 1
      }));
    } catch (error) {
      console.error('Failed to claim job:', error);
      toast.error(error.response?.data?.detail || 'Failed to pick up job');
    } finally {
      setClaiming(null);
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchTerm || 
      job.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || job.service_type === filterType;
    
    return matchesSearch && matchesType;
  });

  // Group urgent jobs (scheduled within 24 hours)
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const urgentJobs = filteredJobs.filter(job => {
    const jobDate = new Date(job.scheduled_date);
    return jobDate <= tomorrow;
  });
  const regularJobs = filteredJobs.filter(job => {
    const jobDate = new Date(job.scheduled_date);
    return jobDate > tomorrow;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <button
          onClick={() => setFilterType('all')}
          className={`p-4 rounded-xl transition-all ${
            filterType === 'all' 
              ? 'bg-gradient-to-br from-[var(--primary)] to-purple-600 text-white shadow-lg scale-105' 
              : 'bg-[var(--card)] border border-[var(--border-primary)] hover:border-[var(--primary)]'
          }`}
        >
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-xs opacity-80">All Jobs</div>
        </button>
        
        {['rides', 'laundry', 'errands', 'courier'].map(type => {
          const Icon = SERVICE_ICONS[type];
          const count = stats.byType[type] || 0;
          return (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`p-4 rounded-xl transition-all ${
                filterType === type 
                  ? `bg-gradient-to-br ${SERVICE_COLORS[type]} text-white shadow-lg scale-105` 
                  : 'bg-[var(--card)] border border-[var(--border-primary)] hover:border-[var(--primary)]'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <span className="text-lg font-bold">{count}</span>
              </div>
              <div className="text-xs opacity-80 capitalize">{type}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
        <Input
          placeholder="Search by service, customer, or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Urgent Jobs Section */}
      {urgentJobs.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-orange-500">
            <Zap className="w-5 h-5" />
            <h3 className="font-semibold">Urgent - Within 24 Hours ({urgentJobs.length})</h3>
          </div>
          <div className="grid gap-3">
            {urgentJobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                isUrgent
                onPickUp={handleQuickPickUp}
                claiming={claiming}
              />
            ))}
          </div>
        </div>
      )}

      {/* Regular Jobs Section */}
      {regularJobs.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-[var(--text-secondary)]">
            Available Jobs ({regularJobs.length})
          </h3>
          <div className="grid gap-3">
            {regularJobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onPickUp={handleQuickPickUp}
                claiming={claiming}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredJobs.length === 0 && (
        <div className="text-center py-12">
          <Briefcase className="w-12 h-12 mx-auto mb-4 text-[var(--text-muted)] opacity-50" />
          <h3 className="text-lg font-semibold text-[var(--text-secondary)]">No Jobs Available</h3>
          <p className="text-[var(--text-muted)] mt-1">
            {filterType !== 'all' 
              ? `No ${filterType} jobs available right now` 
              : 'All jobs have been assigned'}
          </p>
          <Button 
            variant="outline" 
            onClick={fetchAvailableJobs}
            className="mt-4"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
};

// Job Card Component
const JobCard = ({ job, isUrgent, onPickUp, claiming }) => {
  const Icon = SERVICE_ICONS[job.service_type] || Briefcase;
  const colorClass = SERVICE_COLORS[job.service_type] || 'from-gray-500 to-slate-500';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border ${
        isUrgent 
          ? 'border-orange-500/50 bg-orange-500/5' 
          : 'border-[var(--border-primary)] bg-[var(--card)]'
      } hover:shadow-lg transition-all`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          {/* Service Icon */}
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          
          {/* Job Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-[var(--text-primary)] truncate">
                {job.service_name || job.service_type}
              </h4>
              {isUrgent && (
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-500 text-white rounded-full">
                  URGENT
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[var(--text-muted)]">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatDate(job.scheduled_date)} {job.scheduled_time && `at ${job.scheduled_time}`}
              </span>
              
              {job.address && (
                <span className="flex items-center gap-1 truncate">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{job.address}</span>
                </span>
              )}
              
              {job.duration_hours && (
                <span className="flex items-center gap-1">
                  <Briefcase className="w-3.5 h-3.5" />
                  {job.duration_hours}h
                </span>
              )}
            </div>
            
            {job.client_name && (
              <p className="text-sm text-[var(--text-secondary)] mt-1">
                Customer: {job.client_name}
              </p>
            )}
          </div>
        </div>
        
        {/* Quick Pick Up Button */}
        <Button
          onClick={() => onPickUp(job.id, job.service_name || job.service_type)}
          disabled={claiming === job.id || !job.can_claim}
          className={`flex-shrink-0 ${
            isUrgent 
              ? 'bg-orange-500 hover:bg-orange-600' 
              : 'bg-gradient-to-r from-[var(--primary)] to-purple-600 hover:opacity-90'
          }`}
          data-testid={`pickup-job-${job.id}`}
        >
          {claiming === job.id ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-1" />
              Pick Up
            </>
          )}
        </Button>
      </div>
    </motion.div>
  );
};

export default PickUpJobsTab;
